import React, { useMemo, useRef, useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { useStudents } from 'context/StudentsContext';
import { computeDateRangePreset } from '../reportUtils';

// ---------------------------------------------------------------------------
// ReportToolbar
//
// The three filters that apply across every report:
//
//   * Preset date range (School year / this quarter / this month / etc.)
//     with an "override" pair of date inputs for custom windows.
//   * Multi-student filter, populated from the real StudentsContext.
//     Empty selection === "all active students".
//   * A quick badge showing whether we're on Supabase or demo mode, so
//     a teacher demoing to a district isn't confused about where the
//     numbers came from.
//
// This replaces the previous version, which had a hardcoded student list
// ("Alex Thompson / Maria Garcia / ...") that never matched a real
// caseload.
// ---------------------------------------------------------------------------

const PRESETS = [
  { value: 'this-week', label: 'This week' },
  { value: 'this-month', label: 'This month' },
  { value: 'last-30-days', label: 'Last 30 days' },
  { value: 'this-quarter', label: 'This quarter' },
  { value: 'school-year', label: 'School year' },
  { value: 'year-to-date', label: 'Year to date' },
  { value: 'custom', label: 'Custom…' },
];

const ReportToolbar = ({
  dateRange,
  onDateRangeChange,
  studentFilter,
  onStudentFilterChange,
}) => {
  const { students, isLive } = useStudents();
  const [preset, setPreset] = useState('school-year');
  const [studentMenuOpen, setStudentMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  const studentOptions = useMemo(
    () =>
      (Array.isArray(students) ? students : [])
        .filter((s) => (s.status || 'active') !== 'archived')
        .map((s) => ({
          value: s.id,
          label:
            `${s.firstName || ''} ${s.lastName || ''}`.trim() ||
            'Unnamed student',
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [students],
  );

  useEffect(() => {
    const onClick = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setStudentMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const applyPreset = (value) => {
    setPreset(value);
    if (value === 'custom') return;
    const range = computeDateRangePreset(value);
    if (range) onDateRangeChange(range);
  };

  const handleCustomFrom = (value) => {
    setPreset('custom');
    onDateRangeChange({ ...dateRange, from: value });
  };

  const handleCustomTo = (value) => {
    setPreset('custom');
    onDateRangeChange({ ...dateRange, to: value });
  };

  const toggleStudent = (id) => {
    const next = new Set(studentFilter || new Set());
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onStudentFilterChange(next);
  };

  const clearStudentFilter = () => {
    onStudentFilterChange(new Set());
  };

  const selectedCount = studentFilter?.size || 0;
  const studentButtonLabel =
    selectedCount === 0
      ? 'All active students'
      : `${selectedCount} student${selectedCount === 1 ? '' : 's'}`;

  return (
    <div className="bg-surface border-b border-border p-4">
      <div className="flex flex-col lg:flex-row lg:items-end gap-3">
        <div className="w-full lg:w-48">
          <Select
            label="Range"
            options={PRESETS}
            value={preset}
            onChange={applyPreset}
          />
        </div>
        <div className="grid grid-cols-2 gap-2 flex-1 min-w-0">
          <Input
            label="From"
            type="date"
            value={dateRange?.from || ''}
            onChange={(e) => handleCustomFrom(e?.target?.value || '')}
          />
          <Input
            label="To"
            type="date"
            value={dateRange?.to || ''}
            onChange={(e) => handleCustomTo(e?.target?.value || '')}
          />
        </div>

        <div className="w-full lg:w-64 relative" ref={dropdownRef}>
          <label className="block text-sm font-medium mb-1 text-foreground">
            Students
          </label>
          <button
            type="button"
            onClick={() => setStudentMenuOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-2 border border-border rounded-md bg-background px-3 py-2 text-sm text-foreground hover:bg-muted/40"
          >
            <span className="truncate">{studentButtonLabel}</span>
            <Icon name="ChevronDown" size={16} className="text-muted-foreground flex-shrink-0" />
          </button>
          {studentMenuOpen && (
            <div className="absolute z-40 mt-1 w-full max-h-72 overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
              <div className="flex items-center justify-between p-2 border-b border-border">
                <span className="text-xs text-muted-foreground">
                  {selectedCount} selected
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearStudentFilter}
                  disabled={selectedCount === 0}
                >
                  Reset (all)
                </Button>
              </div>
              {studentOptions.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground text-center">
                  No students yet. Add one from the Dashboard.
                </p>
              ) : (
                <ul className="py-1">
                  {studentOptions.map((opt) => {
                    const checked = studentFilter?.has(opt.value) || false;
                    return (
                      <li key={opt.value}>
                        <button
                          type="button"
                          onClick={() => toggleStudent(opt.value)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted/60"
                        >
                          <span
                            className={`w-4 h-4 rounded border flex items-center justify-center ${
                              checked
                                ? 'bg-primary border-primary text-white'
                                : 'border-border bg-background'
                            }`}
                          >
                            {checked && <Icon name="Check" size={12} />}
                          </span>
                          <span className="flex-1 truncate">{opt.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col justify-end">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium border ${
              isLive
                ? 'bg-green-50 text-green-700 border-green-200'
                : 'bg-amber-50 text-amber-700 border-amber-200'
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                isLive ? 'bg-green-500' : 'bg-amber-500'
              }`}
            />
            {isLive ? 'Live data' : 'Demo data'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ReportToolbar;
